/* eslint-disable consistent-return */
import produce from 'immer';
import _ from 'lodash';

import { clearIntercomJwt } from '@/utils';
import accessTokenService from 'utils/accessTokenService';
import { TNC_PROVIDERS } from 'utils/constants';

import {
  ADD_TOAST_NOTIFICATION,
  allowedIndustrySegmentCodes,
  ATTEMPT_UPDATE_MEMBER_PREFERENCES,
  ATTEMPT_UPDATE_TRIP_PREFERENCES,
  CLEAR_ALL_TOAST_NOTIFICATIONS,
  CREATE_ACCESS_TOKEN,
  CREATE_ACCESS_TOKEN_ERROR,
  CREATE_ACCESS_TOKEN_SUCCESS,
  FETCH_LYFT_DRIVER_AVAILABILITY,
  FETCH_LYFT_DRIVER_AVAILABILITY_ERROR,
  FETCH_LYFT_DRIVER_AVAILABILITY_SUCCESS,
  GET_LYFT_TRIP_ESTIMATION,
  GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS,
  GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS_ERROR,
  GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS_SUCCESS,
  GET_MEMBER_PREFERENCES_ERROR,
  GET_MEMBER_PREFERENCES_SUCCESS,
  GET_PROCREDEX_CONNECTION_STATUS_SUCCESS,
  GET_TRIP_APPROVAL_REASON_SUCCESS,
  GET_TRIP_ASSIGNMENT_PREFERENCE_SUCCESS,
  GET_TRIP_CANCELLATION_REASONS_SUCCESS,
  GET_TRIP_DENIAL_REASONS_SUCCESS,
  GET_TRIP_ESCALATION_REASON_SUCCESS,
  GET_TRIP_GROUPINGS,
  GET_TRIP_GROUPINGS_ERROR,
  GET_TRIP_GROUPINGS_SUCCESS,
  GET_TRIP_PREFERENCES,
  GET_TRIP_PREFERENCES_ERROR,
  GET_TRIP_PREFERENCES_SUCCESS,
  GET_UBER_TRIP_ESTIMATION,
  GET_UNREAD_NOTIFICATIONS,
  GET_UNREAD_NOTIFICATIONS_ERROR,
  GET_UNREAD_NOTIFICATIONS_SUCCESS,
  GET_VEHICLE_CATEGORIES,
  GET_VEHICLE_CATEGORIES_ERROR,
  GET_VEHICLE_CATEGORIES_SUCCESS,
  GLOBAL_SEARCH,
  GLOBAL_SEARCH_ERROR,
  GLOBAL_SEARCH_SUCCESS,
  ON_ADD_ACTION_ALERT,
  ON_ADD_ALERT_MSG,
  ON_ADD_APP_NOTIFICATION,
  ON_ADD_ERROR,
  ON_ADD_SUCCESS_MSG,
  ON_CHANGE_MENU,
  ON_CHANGE_TAB,
  ON_CLEAR_ALL_ACTION_ALERT,
  ON_CLEAR_ESTIMATES,
  ON_FETCH_APPOINTMENT_REASONS,
  ON_FETCH_APPOINTMENT_REASONS_ERROR,
  ON_FETCH_APPOINTMENT_REASONS_SUCCESS,
  ON_FETCH_ASSIGNEES,
  ON_FETCH_ASSIGNEES_FAILED,
  ON_FETCH_ASSIGNEES_SUCCESS,
  ON_FETCH_CONTRACTS,
  ON_FETCH_CONTRACTS_ERROR,
  ON_FETCH_CONTRACTS_SUCCESS,
  ON_FETCH_ESTIMATE_ERROR,
  ON_FETCH_ESTIMATE_SUCCESS,
  ON_FETCH_TNC_CONTRACTS_SUCCESS,
  ON_GET_LEVELS_OF_SERVICE,
  ON_GET_LEVELS_OF_SERVICE_ERROR,
  ON_GET_LEVELS_OF_SERVICE_SUCCESS,
  ON_GET_LOCATION_MODIFIERS_SUCCESS,
  ON_LD_CONTEXT_IDENTIFIED,
  ON_LOAD_ORGANIZATIONS,
  ON_LOAD_ORGANIZATIONS_ERROR,
  ON_LOAD_ORGANIZATIONS_SUCCESS,
  ON_LOAD_ROLES_SUCCESS,
  ON_LOAD_USER,
  ON_LOAD_USER_ERROR,
  ON_LOAD_USER_SUCCESS,
  ON_LOGOUT,
  ON_LOGOUT_SUCCESS,
  ON_MARK_ALL_AS_READ_SUCCESS,
  ON_REMOVE_ACTION_ALERT,
  ON_REMOVE_APP_NOTIFICATION,
  ON_REMOVE_OLDEST_MESSAGE,
  ON_REMOVE_ONE_MESSAGE,
  ON_RESET_LOADING_STATE,
  ON_SET_ACTIVE_ORGANIZATION,
  ON_SET_EXPANDED,
  ON_SET_INACTIVITY_MODAL_VISIBILITY,
  ON_SET_KINETIK_PROXY_TOKEN,
  ON_SET_LYFT_TOKEN,
  ON_SET_UBER_TOKEN,
  OPEN_GLOBAL_SEARCH_BOX,
  REMOVE_GLOBAL_SEARCH_VALUE,
  REMOVE_SELECTED_FILTER_DATA,
  REMOVE_TOAST_NOTIFICATION,
  RESET_GLOBAL_SEARCH_RESULTS,
  SELECT_FILTER_DATA,
  SET_GLOBAL_SEARCH_INPUT_VALUE,
  SET_SIDEBAR_MENU_COUNT,
  SET_UNREAD_MESSAGE_COUNT,
  UPDATE_GLOBAL_SEARCHBAR_KEY,
  UPDATE_LEVELS_OF_SERVICE,
  UPDATE_TOAST_NOTIFICATION,
  UPDATE_TRIP_PREFERENCES_FAILED,
  UPDATE_TRIP_PREFERENCES_SUCCESS,
} from './constants';

export const APP_INITIAL_STATE = {
  loading: false,
  isLoadingUser: false,
  isLoadingOrganizations: false,
  isLoadingAccessToken: false,
  messages: [],
  redirectUrl: null,
  redirectPageName: '',
  alertMessages: [],
  toastNotifications: [],
  appNotifications: [],
  isForceLoginLoading: false,
  user: null,
  unreadNotifications: {
    isLoading: false,
    notifications: [],
    hasUnread: false,
  },
  organizations: [],
  selectedLocation: null,
  inactivityModalVisibility: false,
  activeOrganization: null,
  activeTab: 'Create',
  activeMenu: '',
  expanded: true,
  selectedUtilityMenu: null,
  membersInfo: {
    members: [],
    isLoading: false,
    searchParams: { searchValue: '' },
    page: 1,
    limit: 10,
    totalMemberCount: 0,
    sortBy: 'name',
    sortOrder: 1,
  },
  accessTokens: {
    lyft: { token: null, expiresAt: 0 },
    uber: { token: null, expiresAt: 0 },
    kinetik: { token: null, expiresAt: 0 },
  },
  locationModifiers: [],
  assignees: {
    data: [],
    isLoading: false,
  },
  contracts: {
    data: [],
    isLoading: false,
  },
  memberPreferences: null,
  isLoadingMemberPreferences: false,
  appointmentReasons: [],
  isLoadingAppointmentReasons: false,
  estimates: {
    isLoading: false,
    lyft: [],
    uber: [],
    driverAvailability: {
      lyft: [],
    },
  },
  tncContracts: {},
  currentUserRole: {},
  // The user key and org App's last finished LaunchDarkly identify() was for;
  // per-org flags aren't the org's values until this matches (TS-9866).
  ldIdentifiedContext: null,
  // TODO: TS-5173: Remove globalSearchFilter
  globalSearchFilter: {
    selectedFilterData: {},
    searchbarKey: 'GlobalSearchBar',
    globalSearchInputValue: '',
    openGlobalSearchbox: false,
  },
  totalUnreadMessageCount: 0,
  tripPreferences: {
    loading: false,
    isUpdating: false,
    data: {},
  },
  tripCancellationReasons: [],
  tripDenialReasons: [],
  tripEscalationReasons: [],
  tripApprovalReasons: [],
  tripAssignmentPreferences: {},
  sidebarMenuCounts: {
    unassignedTrips: 0,
    needsActionTrips: 0,
    requestsTrips: 0,
  },
  levelsOfServiceStates: {
    isLoading: false,
    data: [],
    totalCount: 0,
    page: 1,
    limit: 20,
  },
  vehicleCategories: {
    isLoading: false,
    data: [],
  },
  integrationState: {
    proCredExConnectionStatus: false,
    jobRegistryEntries: [],
    totalAvailable: 0,
  },
  tripGroupingState: {
    tripGroupings: [],
    isLoadingTripGroupings: false,
  },
  memberEnrollmentCoverageTripCounts: {
    isLoading: false,
    memberUuid: null,
    enrollmentCoverageTripCounts: {
      /*
      [groupNumber]: {
				[coverageProgramIdentificationNumber:coverageStartDate:coverageEndDate]: {
					customContextualAttributes (contains limit),
					coveredTripClassifications: [‘NMT’, ‘NEMT’],
          tripCountsByYear: {
            [year]: {
              numCompletedTrips,
              numScheduledTrips,
              numTotalTrips,
            }
          },
				},
			},
      */
    },
  },
  globalSearch: {
    searchValue: '',
    isLoading: false,
    members: {
      data: [],
      totalAvailable: 0,
    },
    trips: {
      data: [],
      totalAvailable: 0,
    },
    requests: {
      data: [],
      totalAvailable: 0,
    },
    providers: {
      data: [],
      totalAvailable: 0,
    },
  },
};

/* eslint-disable no-param-reassign */
// eslint-disable-next-line default-param-last
const appReducer = (state = APP_INITIAL_STATE, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case GET_LYFT_TRIP_ESTIMATION:
      case GET_UBER_TRIP_ESTIMATION:
      case FETCH_LYFT_DRIVER_AVAILABILITY:
        draft.estimates.isLoading = true;
        break;
      case ON_CLEAR_ESTIMATES:
        draft.estimates = APP_INITIAL_STATE.estimates;
        break;
      case ON_FETCH_TNC_CONTRACTS_SUCCESS:
        draft.tncContracts = action.contracts;
        break;
      case ON_FETCH_ESTIMATE_SUCCESS:
        if (action.tp === TNC_PROVIDERS.UBER) {
          draft.estimates.uber = action.data;
        }
        if (action.tp === TNC_PROVIDERS.LYFT) {
          draft.estimates.lyft = action.data;
        }
        draft.estimates.isLoading = false;
        break;
      case FETCH_LYFT_DRIVER_AVAILABILITY_SUCCESS:
        draft.estimates.driverAvailability.lyft = action.availability;
        draft.estimates.isLoading = false;
        break;
      case ON_FETCH_ESTIMATE_ERROR:
      case FETCH_LYFT_DRIVER_AVAILABILITY_ERROR:
        draft.estimates.isLoading = false;
        break;
      case ON_RESET_LOADING_STATE:
        draft.loading = false;
        draft.isLoadingUser = false;
        draft.isLoadingAccessToken = false;
        draft.isLoadingOrganizations = false;
        break;
      case ON_LOAD_ORGANIZATIONS:
        draft.isLoadingOrganizations = true;
        break;
      case CREATE_ACCESS_TOKEN:
        draft.isLoadingAccessToken = true;
        break;
      case ON_LOAD_USER:
        draft.isLoadingUser = true;
        break;
      case ON_LOAD_USER_SUCCESS:
        draft.isLoadingUser = false;
        draft.user = action.user;
        draft.isLoadingUser = false;
        break;
      case CREATE_ACCESS_TOKEN_SUCCESS:
        accessTokenService.setAccessToken(action.accessToken);
        draft.isLoadingAccessToken = false;
        break;
      case ON_LOAD_ORGANIZATIONS_SUCCESS:
        draft.isLoadingOrganizations = false;
        draft.organizations = action.organizations;
        draft.isLoadingOrganizations = false;
        break;
      case ON_LOGOUT:
        draft.loading = true;
        break;
      case ON_LOAD_ORGANIZATIONS_ERROR:
        draft.isLoadingOrganizations = false;
        break;
      case CREATE_ACCESS_TOKEN_ERROR:
      case ON_LOAD_USER_ERROR:
        accessTokenService.removeAccessToken();
        return APP_INITIAL_STATE;
      case ON_LOGOUT_SUCCESS:
        accessTokenService.removeAccessToken();
        clearIntercomJwt();
        return APP_INITIAL_STATE;
      case ON_ADD_ERROR:
        draft.messages = [
          ...draft.messages.slice(-4),
          {
            message: action.message,
            type: 'error',
            id: Math.random().toString(20),
          },
        ];
        break;
      case ON_ADD_SUCCESS_MSG:
        draft.messages = [
          ...draft.messages.slice(-4),
          {
            message: action.message,
            type: 'success',
            id: Math.random().toString(20),
            removeCopyMessage: action.removeCopyMessage,
          },
        ];
        break;
      case ON_ADD_ALERT_MSG:
        draft.messages = [
          ...draft.messages.slice(-4),
          {
            message: action.message,
            type: 'alert',
            id: Math.random().toString(20),
          },
        ];
        draft.redirectUrl = action.redirectUrl;
        draft.redirectPageName = action.redirectPageName;
        break;
      case ON_REMOVE_ONE_MESSAGE:
        draft.messages.splice(
          draft.messages.indexOf(
            draft.messages.filter((message) => message.id === action.id),
          ),
          1,
        );
        break;
      case ON_REMOVE_OLDEST_MESSAGE:
        // eslint-disable-next-line no-case-declarations
        const tempList = draft.messages;
        tempList.shift();
        draft.messages = tempList;
        break;
      case ON_CHANGE_TAB:
        draft.activeTab = action.tab;
        break;
      case ON_CHANGE_MENU:
        draft.activeMenu = action.menu;
        break;
      case ON_SET_EXPANDED:
        draft.expanded = action.expanded;
        break;
      case ON_SET_INACTIVITY_MODAL_VISIBILITY:
        draft.inactivityModalVisibility = action.visibility;
        break;
      case ON_SET_LYFT_TOKEN:
        draft.accessTokens.lyft = {
          token: action.token,
          expiresAt: new Date().getTime() + (action.expiresAt - 10) * 1000,
        };
        break;
      case ON_SET_UBER_TOKEN:
        draft.accessTokens.uber = {
          token: action.token,
          expiresAt: new Date().getTime() + (action.expiresAt - 10) * 1000,
        };
        break;
      case ON_SET_KINETIK_PROXY_TOKEN:
        draft.accessTokens.kinetik = {
          token: action.token,
          expiresAt: new Date().getTime() + (action.expiresIn - 10) * 1000,
        };
        break;
      case ON_SET_ACTIVE_ORGANIZATION:
        draft.activeOrganization = draft.organizations.find(
          ({ orgResourceId, industrySegment }) =>
            orgResourceId === action.orgResourceId &&
            allowedIndustrySegmentCodes.includes(industrySegment),
        );
        break;
      case ON_MARK_ALL_AS_READ_SUCCESS:
        draft.unreadNotifications.notifications = [];
        draft.unreadNotifications.hasUnread = false;
        break;
      case GET_UNREAD_NOTIFICATIONS:
        draft.unreadNotifications.isLoading = true;
        break;
      case GET_UNREAD_NOTIFICATIONS_SUCCESS:
        draft.unreadNotifications.isLoading = false;
        draft.unreadNotifications.notifications = action.notifications;
        draft.unreadNotifications.hasUnread = action.hasUnread;
        break;
      case GET_UNREAD_NOTIFICATIONS_ERROR:
        draft.unreadNotifications.isLoading = false;
        break;
      case ON_ADD_APP_NOTIFICATION:
        draft.appNotifications.push(action.notification);
        break;
      case ON_REMOVE_APP_NOTIFICATION:
        draft.appNotifications.pop();
        break;
      case ON_GET_LOCATION_MODIFIERS_SUCCESS:
        draft.locationModifiers = action.data;
        break;
      case ON_FETCH_ASSIGNEES:
        draft.assignees.isLoading = true;
        break;
      case ON_FETCH_ASSIGNEES_SUCCESS:
        draft.assignees.isLoading = false;
        draft.assignees.data = action.assignees;
        break;
      case ON_FETCH_ASSIGNEES_FAILED:
        draft.assignees.isLoading = false;
        break;
      case ON_FETCH_CONTRACTS:
        draft.contracts.isLoading = true;
        break;
      case ON_FETCH_CONTRACTS_SUCCESS:
        draft.contracts.isLoading = false;
        draft.contracts.data = action.contracts;
        break;
      case ON_FETCH_CONTRACTS_ERROR:
        draft.contracts.isLoading = false;
        break;
      case ATTEMPT_UPDATE_MEMBER_PREFERENCES:
        draft.isLoadingMemberPreferences = true;
        if (action?.body?.isActive !== undefined)
          draft.memberPreferences.isActive = action.body.isActive;
        break;
      case GET_MEMBER_PREFERENCES_SUCCESS:
        draft.memberPreferences = action.memberPreferences;
        draft.isLoadingMemberPreferences = false;
        break;
      case GET_MEMBER_PREFERENCES_ERROR:
        draft.isLoadingMemberPreferences = false;
        break;
      case ON_ADD_ACTION_ALERT:
        draft.alertMessages.push({
          uuid: Math.random().toString(20),
          alertType: action.alertType,
          message: action.message,
          action: action.action,
        });
        break;
      case ON_REMOVE_ACTION_ALERT:
        draft.alertMessages = draft.alertMessages.filter(
          (message) => message.uuid !== action.uuid,
        );
        break;
      case ON_CLEAR_ALL_ACTION_ALERT:
        draft.alertMessages = [];
        break;
      case ON_FETCH_APPOINTMENT_REASONS:
        draft.isLoadingAppointmentReasons = true;
        break;
      case ON_FETCH_APPOINTMENT_REASONS_SUCCESS:
        draft.appointmentReasons = action.appointmentReasons;
        draft.isLoadingAppointmentReasons = false;
        break;
      case ON_FETCH_APPOINTMENT_REASONS_ERROR:
        draft.isLoadingAppointmentReasons = false;
        break;
      case ON_LOAD_ROLES_SUCCESS:
        // The role is re-read on every navigation (TS-9877); keep the same
        // object when nothing changed so permission consumers don't re-render.
        if (!_.isEqual(state.currentUserRole, action.role)) {
          draft.currentUserRole = action.role;
        }
        break;
      case ON_LD_CONTEXT_IDENTIFIED:
        draft.ldIdentifiedContext = action.context;
        break;
      case SELECT_FILTER_DATA:
        // TODO: TS-5173: Remove globalSearchFilter
        draft.globalSearchFilter.selectedFilterData = action.selectedFilterData;
        break;
      case REMOVE_SELECTED_FILTER_DATA:
        // TODO: TS-5173: Remove globalSearchFilter
        draft.globalSearchFilter.selectedFilterData = {};
        break;
      case UPDATE_GLOBAL_SEARCHBAR_KEY:
        // TODO: TS-5173: Remove globalSearchFilter
        draft.globalSearchFilter.searchbarKey = `GlobalSearchBar${Math.random()}`;
        break;
      case REMOVE_GLOBAL_SEARCH_VALUE:
        // TODO: TS-5173: Remove globalSearchFilter
        draft.globalSearchFilter = APP_INITIAL_STATE.globalSearchFilter;
        break;
      case SET_UNREAD_MESSAGE_COUNT:
        draft.totalUnreadMessageCount = action.unreadMessageCount;
        break;
      case SET_GLOBAL_SEARCH_INPUT_VALUE:
        // TODO: TS-5173: Remove globalSearchFilter
        draft.globalSearchFilter.globalSearchInputValue = action.value;
        break;
      case UPDATE_LEVELS_OF_SERVICE:
        draft.levelsOfServiceStates.data = draft.levelsOfServiceStates.data.map(
          (los) => (los.uuid === action.data.uuid ? action.data : los),
        );
        break;
      case OPEN_GLOBAL_SEARCH_BOX:
        // TODO: TS-5173: Remove globalSearchFilter
        draft.globalSearchFilter.openGlobalSearchbox = action.value;
        break;
      case GET_TRIP_PREFERENCES:
        draft.tripPreferences.loading = true;
        break;
      case GET_TRIP_PREFERENCES_SUCCESS:
        draft.tripPreferences.loading = false;
        draft.tripPreferences.data = action.tripPreferences;
        break;
      case GET_TRIP_PREFERENCES_ERROR:
        draft.tripPreferences.loading = false;
        break;
      case ATTEMPT_UPDATE_TRIP_PREFERENCES:
        draft.tripPreferences.isUpdating = true;
        break;
      case UPDATE_TRIP_PREFERENCES_SUCCESS:
        draft.tripPreferences.data = action.updatedTripPreferences;
        draft.tripPreferences.isUpdating = false;
        break;
      case UPDATE_TRIP_PREFERENCES_FAILED:
        draft.tripPreferences.isUpdating = false;
        break;
      case GET_TRIP_CANCELLATION_REASONS_SUCCESS:
        draft.tripCancellationReasons = action.tripCancellations;
        break;
      case GET_TRIP_DENIAL_REASONS_SUCCESS:
        draft.tripDenialReasons = action.tripDenials;
        break;
      case GET_TRIP_ASSIGNMENT_PREFERENCE_SUCCESS:
        draft.tripAssignmentPreferences = action.preferences;
        break;
      case SET_SIDEBAR_MENU_COUNT:
        draft.sidebarMenuCounts[action.key] = action.value;
        break;
      case ON_GET_LEVELS_OF_SERVICE:
        draft.levelsOfServiceStates.isLoading = true;
        draft.levelsOfServiceStates.page = action.params.page;
        break;
      case ON_GET_LEVELS_OF_SERVICE_SUCCESS:
        draft.levelsOfServiceStates.data = action.replace
          ? action.data
          : [...draft.levelsOfServiceStates.data, ...action.data];
        draft.levelsOfServiceStates.totalCount = action.totalAvailable;
        draft.levelsOfServiceStates.isLoading = false;
        break;
      case ON_GET_LEVELS_OF_SERVICE_ERROR:
        draft.levelsOfServiceStates.isLoading = false;
        break;
      case GET_VEHICLE_CATEGORIES:
        draft.vehicleCategories.isLoading = true;
        break;
      case GET_VEHICLE_CATEGORIES_SUCCESS:
        draft.vehicleCategories.data = action.categories;
        draft.vehicleCategories.isLoading = false;
        break;
      case GET_VEHICLE_CATEGORIES_ERROR:
        draft.vehicleCategories.isLoading = false;
        break;
      case GET_PROCREDEX_CONNECTION_STATUS_SUCCESS:
        draft.integrationState.jobRegistryEntries = action.jobRegistryEntries;
        draft.integrationState.totalAvailable = action.totalAvailable;
        draft.integrationState.proCredExConnectionStatus =
          action.jobRegistryEntries.length > 0;
        break;
      case GET_TRIP_ESCALATION_REASON_SUCCESS:
        draft.tripEscalationReasons = action.tripEscalationReasons;
        break;
      case GET_TRIP_APPROVAL_REASON_SUCCESS:
        draft.tripApprovalReasons = action.tripApprovalReasons;
        break;
      case GET_TRIP_GROUPINGS:
        draft.tripGroupingState.isLoadingTripGroupings = true;
        break;
      case GET_TRIP_GROUPINGS_SUCCESS:
        draft.tripGroupingState.isLoadingTripGroupings = false;
        draft.tripGroupingState.tripGroupings = action.tripGroupings;
        break;
      case GET_TRIP_GROUPINGS_ERROR:
        draft.tripGroupingState.isLoadingTripGroupings = false;
        break;
      case GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS:
        draft.memberEnrollmentCoverageTripCounts.isLoading = true;
        break;
      case GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS_SUCCESS:
        draft.memberEnrollmentCoverageTripCounts.isLoading = false;

        if (
          action.memberUuid ===
          draft.memberEnrollmentCoverageTripCounts.memberUuid
        ) {
          // Same member, merge the coverage trip counts instead of replacing
          _.merge(
            draft.memberEnrollmentCoverageTripCounts
              .enrollmentCoverageTripCounts,
            action.enrollmentCoverageTripCounts,
          );
        } else {
          draft.memberEnrollmentCoverageTripCounts.enrollmentCoverageTripCounts =
            action.enrollmentCoverageTripCounts;

          // Update member UUID
          draft.memberEnrollmentCoverageTripCounts.memberUuid =
            action.memberUuid;
        }
        break;
      case GET_MEMBER_ENROLLMENT_COVERAGE_TRIP_COUNTS_ERROR:
        draft.memberEnrollmentCoverageTripCounts.isLoading = false;
        break;
      case GLOBAL_SEARCH:
        draft.globalSearch.searchValue = action.searchValue;
        draft.globalSearch.isLoading = true;
        break;
      case GLOBAL_SEARCH_SUCCESS:
        draft.globalSearch.members = {
          ...draft.globalSearch.members,
          ...action.memberResults,
        };
        draft.globalSearch.trips = {
          ...draft.globalSearch.trips,
          ...action.tripResults,
        };
        draft.globalSearch.requests = {
          ...draft.globalSearch.requests,
          ...action.requestResults,
        };
        draft.globalSearch.providers = {
          ...draft.globalSearch.providers,
          ...action.providerResults,
        };
        draft.globalSearch.isLoading = false;
        break;
      case GLOBAL_SEARCH_ERROR:
        draft.globalSearch.isLoading = false;
        break;
      case RESET_GLOBAL_SEARCH_RESULTS:
        draft.globalSearch = APP_INITIAL_STATE.globalSearch;
        break;
      case ADD_TOAST_NOTIFICATION:
        draft.toastNotifications.push(action.notification);
        break;
      case UPDATE_TOAST_NOTIFICATION: {
        const toast = draft.toastNotifications.find(
          (notification) => notification.uuid === action.uuid,
        );
        if (toast) Object.assign(toast, action.notification);
        break;
      }
      case REMOVE_TOAST_NOTIFICATION:
        draft.toastNotifications = draft.toastNotifications.filter(
          (notification) => notification.uuid !== action.uuid,
        );
        break;
      case CLEAR_ALL_TOAST_NOTIFICATIONS:
        draft.toastNotifications = [];
        break;
      default:
        break;
    }
  });

export default appReducer;
